import i18n, { type InitOptions } from "i18next";
import { initReactI18next } from "react-i18next";

import {
  DefaultNamespace,
  FallbackLanguage,
  SupportedLanguages,
  type LangResources,
  ComputeFallbackLanguageForTest,
  generateCommonResourceNamespaces
} from "./i18nConstants";

/**
 * i18n initialization for the Sage E-Invoicing app (single-app, no MFE).
 *
 * - Loads all translations via Vite's import.meta.glob (no runtime fetch)
 * - Falls back through language candidates: exact → base lang → fallback
 * - Namespace is used as-is (e.g., "base", "words", etc.)
 */

const commonLocaleModules = import.meta.glob<Record<string, string>>(
  "../assets/locales/common/**/*.json"
);

const featureLocaleModules = import.meta.glob<Record<string, string>>(
  "../assets/locales/features/**/*.json"
);

const isDevEnvironment = import.meta.env.DEV;

const generateCandidates = (lang: string): string[] => {
  const candidates: string[] = [];

  candidates.push(lang);

  if (lang.includes("-")) {
    const short = lang.split("-")[0];
    candidates.push(short);
  }

  candidates.push(FallbackLanguage);

  return Array.from(new Set(candidates));
};

async function loadLocaleResource<Namespace extends keyof LangResources>(
  language: string,
  namespace: string,
  callback: (
    errorValue: unknown,
    translations: null | LangResources[Namespace]
  ) => void
): Promise<void> {
  try {
    const candidates = generateCandidates(language);
    let loader: (() => Promise<LangResources[Namespace]>) | undefined;

    for (const candidate of candidates) {
      // Try common path first: locales/common/{lang}/{namespace}.json
      const commonKey = `../assets/locales/common/${candidate}/${namespace}.json`;
      if (commonLocaleModules[commonKey]) {
        loader = commonLocaleModules[commonKey] as () => Promise<
          LangResources[Namespace]
        >;
        break;
      }

      // Try feature path: locales/features/{namespace}/{lang}.json
      const featureKey = `../assets/locales/features/${namespace}/${candidate}.json`;
      if (featureLocaleModules[featureKey]) {
        loader = featureLocaleModules[featureKey] as () => Promise<
          LangResources[Namespace]
        >;
        break;
      }
    }

    if (!loader) {
      throw new Error(
        `Locale not found for language "${language}", namespace "${namespace}"`
      );
    }

    const resources = await loader();
    callback(null, resources);
  } catch (err) {
    callback(err, null);
  }
}

export const i18nOptions: InitOptions = {
  debug: isDevEnvironment,
  partialBundledLanguages: true,
  ns: generateCommonResourceNamespaces(),
  defaultNS: DefaultNamespace,
  supportedLngs: SupportedLanguages,
  fallbackLng: ComputeFallbackLanguageForTest(),
  load: "all",
  interpolation: {
    escapeValue: false
  },
  react: {
    bindI18n: "languageChanged loaded"
  },
  resources: {}
};

i18n
  .use(initReactI18next)
  .use({
    type: "backend" as const,
    read<Namespace extends keyof LangResources>(
      language: string,
      namespace: Namespace,
      callback: (
        errorValue: unknown,
        translations: null | LangResources[Namespace]
      ) => void
    ) {
      loadLocaleResource(language, namespace as string, callback);
    }
  })
  .init(i18nOptions);

// Custom formatters matching the monorepo pattern
if (i18n.services.formatter) {
  const spacePattern = /\s+/g;
  i18n.services.formatter.add("lowercase", (value) => {
    return value.toLowerCase();
  });
  i18n.services.formatter.add("upperCase", (value) => {
    return value.toUpperCase();
  });
  i18n.services.formatter.add("underscore", (value) => {
    return value.replace(spacePattern, "_");
  });
}

export const i18nGlobal = i18n;
